import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Question } from "@phosphor-icons/react";
import { forwardRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Tooltip } from "@/components/ui/overlays/Tooltip";

export type OptionCardItem = {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  helpText?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
};

export type OptionCardGroupProps = Omit<RadioGroupPrimitive.RadioGroupProps, "children"> & {
  options: OptionCardItem[];
  error?: boolean;
  columns?: 1 | 2 | 3;
};

const columnsClassName = {
  1: "bfrs-grid-cols-1",
  2: "bfrs-grid-cols-1 md:bfrs-grid-cols-2",
  3: "bfrs-grid-cols-1 md:bfrs-grid-cols-3"
};

export const OptionCardGroup = forwardRef<HTMLDivElement, OptionCardGroupProps>(({ className, options, error, columns = 2, ...props }, ref) => (
  <RadioGroupPrimitive.Root ref={ref} className={cn("bfrs-grid bfrs-gap-3", columnsClassName[columns], className)} aria-invalid={error} {...props}>
    {options.map((option) => (
      <RadioGroupPrimitive.Item key={option.value} value={option.value} disabled={option.disabled} asChild>
        <button
          type="button"
          className={cn(
            "bfrs-group bfrs-flex bfrs-min-h-[76px] bfrs-w-full bfrs-items-start bfrs-gap-3 bfrs-rounded-xl bfrs-border bfrs-bg-surface-card bfrs-px-4 bfrs-py-3 bfrs-text-left bfrs-outline-none bfrs-transition hover:bfrs-border-primary/70 hover:bfrs-bg-primary-soft/40 focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/25 data-[state=checked]:bfrs-border-primary data-[state=checked]:bfrs-bg-primary-soft disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-55",
            error ? "bfrs-border-danger" : "bfrs-border-border-control"
          )}
        >
          {option.icon && <span className="bfrs-mt-0.5 bfrs-flex bfrs-size-8 bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-lg bfrs-bg-surface-muted bfrs-text-primary">{option.icon}</span>}
          <span className="bfrs-min-w-0 bfrs-flex-1">
            <span className="bfrs-flex bfrs-min-w-0 bfrs-items-center bfrs-gap-1.5">
              <span className="bfrs-truncate bfrs-text-sm bfrs-font-semibold bfrs-text-text-primary">{option.label}</span>
              {option.helpText && (
                <Tooltip content={option.helpText}>
                  <span className="bfrs-inline-flex bfrs-size-4 bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-pill bfrs-text-text-muted hover:bfrs-text-text-primary">
                    <Question size={13} weight="bold" />
                  </span>
                </Tooltip>
              )}
            </span>
            {option.description && <span className="bfrs-mt-1 bfrs-block bfrs-text-xs bfrs-leading-5 bfrs-text-text-secondary">{option.description}</span>}
          </span>
          <span className="bfrs-mt-0.5 bfrs-flex bfrs-size-5 bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-pill bfrs-border bfrs-border-border-control bfrs-bg-surface-card group-data-[state=checked]:bfrs-border-primary">
            <RadioGroupPrimitive.Indicator className="bfrs-size-2.5 bfrs-rounded-pill bfrs-bg-primary" />
          </span>
        </button>
      </RadioGroupPrimitive.Item>
    ))}
  </RadioGroupPrimitive.Root>
));

OptionCardGroup.displayName = "OptionCardGroup";
